// Based on TiMessengerTestTreiber.yaml 2.0.0
import { inject, Injectable } from '@angular/core';
import { MatrixService } from '../matrix/matrix.service';
import { AccountDataEvents } from 'matrix-js-sdk/lib/@types/event';
import { AuthorizationList } from '../../../shared-api/authorizationList';
import {
  CannotAddAllowedUsersInAllowAllModeError,
  CannotAddBlockedUsersInBlockAllModeError,
  CannotDeleteAllowedUsersInAllowAllModeError,
  CannotDeleteBlockedUsersInBlockAllModeError,
  CannotGetAllowedUsersForModeAllowAllError,
  CannotGetBlockedUsersForModeBlockAllError,
  InvalidParameterError
} from '../../errors/authorizationManagement.errors';
import { AuthorizationMode } from '../../../shared-api/authorizationMode';
import { AuthorizationManagementService as ApiAuthorizationManagementService } from '../../../shared-api/authorizationManagement.service';
import { MatrixError } from 'matrix-js-sdk';
import { matrixErrorAsJson } from '../../errors/error-with-status';
import { AuthMode } from '../settings/settings.service';
import { BehaviorSubject, map } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
/**
 * Der AuthorizationManagementService stellt eine Möglichkeit zum Zulassen und Blockieren von Nutzern und Servern bereit.
 * Implementation of the mandatory AuthorizationManagement API of the gematik TI-M Testtreiber
 * Umgesetze AFOs:
 * A_25363 - Testtreiber-Modul
 * A_25361 - keine Modifikation der Inhalte
 */
export class AuthorizationManagementService implements ApiAuthorizationManagementService {
  private readonly _config$ = new BehaviorSubject<PermissionConfiguration>({
    defaultSetting: AuthMode.ALLOW_ALL,
    serverExceptions: {},
    userExceptions: {},
    groupExceptions: []
  });
  private readonly config$ = this._config$.asObservable();
  readonly authMode$ = this.config$.pipe(map(config => config.defaultSetting));
  readonly users$ = this.config$.pipe(map(config => Object.keys(config.userExceptions)));
  readonly servers$ = this.config$.pipe(map(config => Object.keys(config.serverExceptions)));
  readonly groups$ = this.config$.pipe(map(config => [...config.groupExceptions]));
  /*
   * Aufteilen von Speichern und Hinzufügen, für Testtreiber beides direkt verwenden,
   *  für eigentliche Anwendung einzeln verwenden
   */
  private readonly matrix = inject(MatrixService);

  get config(): PermissionConfiguration {
    return this._config$.value;
  }

  private static asAuthorizationList(
    serverExceptions: ExceptionMap,
    userExceptions: ExceptionMap,
    groupExceptions: GroupException[]
  ): AuthorizationList {
    const authList: AuthorizationList = {
      mxids: [],
      serverNames: [],
      groupNames: groupExceptions.map(g => g.groupName)
    };
    if (userExceptions && Object.keys(userExceptions).length !== 0) {
      authList.mxids = Object.keys(userExceptions).map(mxid => ({ mxid }));
    }
    if (serverExceptions && Object.keys(serverExceptions).length !== 0) {
      authList.serverNames = Object.keys(serverExceptions).map(serverName => ({
        serverName
      }));
    }
    return authList;
  }

  private static setKeysIn(exceptions: ExceptionMap, keys: string[]): void {
    for (const key of keys) {
      exceptions[key] = {};
    }
  }

  private static unsetKeysIn(exceptions: ExceptionMap, keys: string[]): void {
    for (const key of keys) {
      delete exceptions[key];
    }
  }

  async fetchConfig(): Promise<void> {
    let config: PermissionConfiguration = {
      defaultSetting: AuthMode.ALLOW_ALL,
      serverExceptions: {},
      userExceptions: {},
      groupExceptions: []
    };

    try {
      const result = (await this.matrix.client.getAccountDataFromServer(
        'de.gematik.tim.account.permissionconfig.pro.v1' as keyof AccountDataEvents
      )) as any;

      if (result) {
        config = this.normalizeConfig(result);
      }
    } catch (error) {
      console.warn('fetchConfig fehlgeschlagen. Setze zurück auf Default', error);
    }

    this.setConfig(config);
  }

  /**
   * Adds the users and/or server-names in the provided list to the list of allowed users and server-names
   * @param authorizationList
   */
  async addAllowedUsersList(authorizationList: AuthorizationList): Promise<AuthorizationList> {
    if (!authorizationList) {
      throw new InvalidParameterError('missing or invalid parameter "authorizationList"').asJsonError();
    }
    if (this._config$.value.defaultSetting === AuthMode.ALLOW_ALL) {
      throw new CannotAddAllowedUsersInAllowAllModeError().asJsonError();
    }

    return await this.takeOverAuthorizationList(authorizationList);
  }

  /**
   * Deletes the users and/or server-names in the provided list from the list of allowed users and server-names
   * @param authorizationList
   */
  async deleteAllowedUsersList(authorizationList: AuthorizationList): Promise<AuthorizationList> {
    if (!authorizationList) {
      throw new InvalidParameterError('missing or invalid parameter "authorizationList"').asJsonError();
    }
    if (this._config$.value.defaultSetting === AuthMode.ALLOW_ALL) {
      throw new CannotDeleteAllowedUsersInAllowAllModeError().asJsonError();
    }

    return await this.deleteAuthorizationList(authorizationList);
  }

  /**
   * Adds the users and/or server-names in the provided list to the list of blocked users and server-names
   * @param authorizationList
   */
  async addBlockedUsersList(authorizationList: AuthorizationList): Promise<AuthorizationList> {
    if (!authorizationList) {
      throw new InvalidParameterError('missing or invalid parameter "authorizationList"').asJsonError();
    }
    if (this._config$.value.defaultSetting === AuthMode.BLOCK_ALL) {
      throw new CannotAddBlockedUsersInBlockAllModeError().asJsonError();
    }

    return await this.takeOverAuthorizationList(authorizationList);
  }

  /**
   * Deletes the users and/or server-names in the provided list from the list of blocked users and server-names
   * @param authorizationList
   */
  async deleteBlockedUsersList(authorizationList: AuthorizationList): Promise<AuthorizationList> {
    if (!authorizationList) {
      throw new InvalidParameterError('missing or invalid parameter "authorizationList"').asJsonError();
    }
    if (this._config$.value.defaultSetting === AuthMode.BLOCK_ALL) {
      throw new CannotDeleteBlockedUsersInBlockAllModeError().asJsonError();
    }

    return await this.deleteAuthorizationList(authorizationList);
  }

  /**
   * Returns a list of all users and server-names, which are allowed to contact the client
   */
  async getAllowedUsersList(): Promise<AuthorizationList> {
    const config = this._config$.value;
    if (config.defaultSetting === AuthMode.ALLOW_ALL) {
      throw new CannotGetAllowedUsersForModeAllowAllError().asJsonError();
    }

    return AuthorizationManagementService.asAuthorizationList(
      config.serverExceptions,
      config.userExceptions,
      config.groupExceptions
    );
  }

  /**
   * Returns a list of all users and server-names, which are not allowed to contact the client
   */
  async getBlockedUsersList(): Promise<AuthorizationList> {
    const config = this._config$.value;
    if (config.defaultSetting === AuthMode.BLOCK_ALL) {
      throw new CannotGetBlockedUsersForModeBlockAllError().asJsonError();
    }

    return AuthorizationManagementService.asAuthorizationList(
      config.serverExceptions,
      config.userExceptions,
      config.groupExceptions
    );
  }

  /**
   * Returns the currently active authorization mode
   */
  async getAuthorizationMode(): Promise<AuthorizationMode> {
    await this.fetchConfig();
    return this._config$.value.defaultSetting === AuthMode.ALLOW_ALL
      ? AuthorizationMode.AllowAll
      : AuthorizationMode.BlockAll;
  }

  /**
   * Selects the authorization mode
   * @param mode the new mode
   */
  async setAuthorizationMode(mode: AuthorizationMode): Promise<AuthorizationMode> {
    if (!mode) {
      throw new InvalidParameterError('missing or invalid parameter "mode"').asJsonError();
    }

    const config: PermissionConfiguration = {
      defaultSetting: mode === AuthorizationMode.AllowAll ? AuthMode.ALLOW_ALL : AuthMode.BLOCK_ALL,
      serverExceptions: {},
      userExceptions: {},
      groupExceptions: []
    };

    this.setConfig(await this.persistOnServer(config));

    return mode;
  }

  private normalizeConfig(result: any): PermissionConfiguration {
    const clone = structuredClone(result);

    if (clone.domainExceptions && (!clone.serverExceptions || Object.keys(clone.serverExceptions).length === 0)) {
      clone.serverExceptions = clone.domainExceptions;
    }

    if (Array.isArray(clone.groupNames) && (!clone.groupExceptions || clone.groupExceptions.length === 0)) {
      clone.groupExceptions = clone.groupNames.map((name: string) => ({
        groupName: name
      }));
    }
    delete clone.groupNames;
    delete clone.domainExceptions;
    return clone as PermissionConfiguration;
  }

  private setConfig(config: PermissionConfiguration): void {
    this._config$.next(config);
  }

  private async persistOnServer(config: PermissionConfiguration): Promise<PermissionConfiguration> {
    try {
      await this.matrix.client.setAccountData(
        'de.gematik.tim.account.permissionconfig.pro.v1' as keyof AccountDataEvents,
        config
      );

      return config;
    } catch (error) {
      console.error('persistOnServer: setAccountData fehlgeschlagen', error);

      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('SetAccountDataError', error);
    }
  }

  private async takeOverAuthorizationList(authorizationList: AuthorizationList): Promise<AuthorizationList> {
    const config = structuredClone(this._config$.value);

    if (authorizationList.mxids) {
      AuthorizationManagementService.setKeysIn(
        config.userExceptions,
        authorizationList.mxids.map(id => id.mxid)
      );
    }
    if (authorizationList.serverNames) {
      AuthorizationManagementService.setKeysIn(
        config.serverExceptions,
        authorizationList.serverNames.map(id => id.serverName)
      );
    }
    if (authorizationList.groupNames) {
      // bestehende und neue Groups zusammenführen
      const groups = new Set([
        ...config.groupExceptions.map(exception => exception.groupName),
        ...authorizationList.groupNames
      ]);
      // doppelte Einträge entfernen
      config.groupExceptions = [...groups].map(groupName => ({ groupName }));
    }

    const newConfig = await this.persistOnServer(config);
    this.setConfig(newConfig);

    return AuthorizationManagementService.asAuthorizationList(
      newConfig.serverExceptions,
      newConfig.userExceptions,
      newConfig.groupExceptions ?? []
    );
  }

  private async deleteAuthorizationList(authorizationList: AuthorizationList): Promise<AuthorizationList> {
    const config = structuredClone(this._config$.value);

    if (authorizationList.mxids) {
      AuthorizationManagementService.unsetKeysIn(
        config.userExceptions,
        authorizationList.mxids.map(id => id.mxid)
      );
    }
    if (authorizationList.serverNames) {
      AuthorizationManagementService.unsetKeysIn(
        config.serverExceptions,
        authorizationList.serverNames.map(id => id.serverName)
      );
    }
    config.groupExceptions = config.groupExceptions.filter(
      exception => !authorizationList.groupNames.includes(exception.groupName)
    );

    const newConfig = await this.persistOnServer(config);
    this.setConfig(newConfig);

    return AuthorizationManagementService.asAuthorizationList(
      newConfig.serverExceptions,
      newConfig.userExceptions,
      newConfig.groupExceptions
    );
  }
}

// https://gemspec.gematik.de/downloads/gemSpec/gemSpec_TI-M_Basis/gemSpec_TI-M_Basis_V1.0.0.pdf
// automatically generated by json-schema-to-typescript.
// from: https://github.com/gematik/api-ti-messenger/blob/main/src/schema/TI-M_Pro/permissionConfig_V1.json
type ExceptionMap = Record<string, Record<string, unknown>>;

export interface GroupException {
  groupName: string;
}

export interface PermissionConfiguration {
  defaultSetting: AuthMode.ALLOW_ALL | AuthMode.BLOCK_ALL;
  /**
   * The map of servers to in-/exclude. This is a mapping of Matrix server name
   * (https://spec.matrix.org/v1.3/appendices/#server-name) to empty object.
   */
  serverExceptions: ExceptionMap;
  /**
   * The map of users to in-/exclude. This is a mapping of Matrix user ID
   * (https://spec.matrix.org/v1.3/appendices/#user-identifiers) to empty object.
   */
  userExceptions: ExceptionMap;
  /**
   * The list of group exceptions to in-/exclude.
   * Gemäß gematik-Schema (A_26390): Array von Objekten mit groupName.
   * Aktuell definierte Gruppen: "isInsuredPerson"
   */
  groupExceptions: GroupException[];
}
